import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  Member,
  MemberLocation,
  MemberService,
  CreateMemberRequest,
  UpdateMemberRequest,
  CredentialsDelivered
} from '../../../core/services/member.service';
import { TenantContextService } from '../../../core/services/tenant-context.service';
import { BusinessUnitStore } from '../../../core/stores/business-unit.store';
import { LocationPickerModalComponent } from './location-picker-modal/location-picker-modal.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { CustomFieldsInputComponent } from '../../../shared/components/custom-fields-input/custom-fields-input.component';
import { EntitlementService } from '../../../core/services/entitlement.service';
import { PlanLimitModalService } from '../../../core/services/plan-limit-modal.service';
import { isGatedErrorCode } from '../../../core/utils/entitlement-labels';

@Component({
  selector: 'app-member-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LocationPickerModalComponent, SpinnerComponent, CustomFieldsInputComponent],
  templateUrl: './member-form.component.html'
})
export class MemberFormComponent implements OnChanges {
  private fb = inject(FormBuilder);
  private memberService = inject(MemberService);
  private tenantContext = inject(TenantContextService);
  private buStore = inject(BusinessUnitStore);
  private entitlement = inject(EntitlementService);
  private planLimitModal = inject(PlanLimitModalService);

  @Input() open = false;
  @Input() member: Member | null = null;

  @ViewChild('cf') cf?: CustomFieldsInputComponent;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<Member>();
  @Output() userProvisioned = new EventEmitter<{
    tempPassword: string | null;
    username: string | null;
    credentialsDelivered: CredentialsDelivered | null;
    linked: boolean;
    reactivated: boolean;
  }>();

  isSubmitting = false;
  errorMessage = '';
  location: MemberLocation | null = null;
  locationModalOpen = false;
  selectedBuIds: Set<string> = new Set();

  get activeUnits() { return this.buStore.activeUnits(); }

  readonly form: FormGroup = this.fb.group({
    fullName:              ['', [Validators.required, Validators.minLength(2)]],
    nic:                   ['', { validators: [Validators.required, Validators.minLength(5)], asyncValidators: [this.uniqueValidator('nic')] }],
    dateOfBirth:           [null],
    gender:                [null],
    occupation:            [''],

    phone:                 ['', { validators: [Validators.required, Validators.minLength(7)], asyncValidators: [this.uniqueValidator('phone')] }],
    email:                 ['', [Validators.email]],
    address:               [''],

    householdId:           [''],
    householdRole:         [null],
    emergencyContactName:  [''],
    emergencyContactPhone: [''],

    joinedDate:            [this.today(), [Validators.required]],
    monthlyFee:            [null],
    status:                ['Active', [Validators.required]],
    notes:                 ['']
  });

  get f() { return this.form.controls; }

  get customFieldsBuId(): string | null {
    const selected = Array.from(this.selectedBuIds);
    return selected.length > 0 ? selected[0] : this.tenantContext.getCurrentBusinessUnitId();
  }

  get isEditMode(): boolean { return !!this.member; }

  get title(): string {
    return this.isEditMode ? 'Edit Member' : 'Add New Member';
  }

  get selectedBuName(): string {
    const buId = this.tenantContext.getCurrentBusinessUnitId();
    if (buId == null) return 'No committee selected';
    const unit = this.buStore.units().find((u: any) => u.id === buId || u.businessUnitId === buId);
    return unit?.name || `Committee #${buId}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      this.buStore.loadOnce();
      this.resetForm();
    }
  }

  // Async per-tenant uniqueness check. Debounces 300ms, skips the row being edited.
  // Returns `{ taken: true }` when the value is already used by another member.
  private uniqueValidator(field: 'phone' | 'nic'): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const raw = (control.value ?? '').toString().trim();
      if (!raw) return of(null);
      const excludeId = this.member?.id ?? null;
      return timer(300).pipe(
        switchMap(() => this.memberService.checkUnique(field, raw, excludeId)),
        map((res: any) => (res?.success && res?.data?.exists) ? { taken: true } : null),
        catchError(() => of(null))
      );
    };
  }

  isBuSelected(id: string): boolean {
    return this.selectedBuIds.has(id);
  }

  toggleBu(id: string): void {
    if (this.selectedBuIds.has(id)) this.selectedBuIds.delete(id);
    else this.selectedBuIds.add(id);
    this.selectedBuIds = new Set(this.selectedBuIds);
  }

  onLocationChange(value: MemberLocation | null): void {
    this.location = value;
  }

  openLocationModal(): void { this.locationModalOpen = true; }
  closeLocationModal(): void { this.locationModalOpen = false; }
  onLocationConfirmed(value: MemberLocation | null): void {
    this.location = value;
    this.locationModalOpen = false;
  }
  clearLocation(): void { this.location = null; }

  close(): void {
    if (this.isSubmitting) return;
    this.closed.emit();
  }

  onSubmit(): void {
    if (this.form.pending) {
      this.errorMessage = 'Still checking NIC / phone uniqueness — please wait a moment.';
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMessage = 'Please fix the highlighted fields.';
      return;
    }

    const buIds = Array.from(this.selectedBuIds);
    if (!this.isEditMode && buIds.length === 0) {
      this.errorMessage = 'Select at least one committee to add the member to.';
      return;
    }

    if (this.cf && !this.cf.valid) {
      this.cf.touch();
      this.errorMessage = 'Please complete the required additional fields.';
      return;
    }

    const v = this.form.value;
    const payloadBase = {
      businessUnitIds:       buIds.length > 0 ? buIds : null,
      fullName:              v.fullName?.trim() || '',
      nic:                   v.nic?.trim() || '',
      address:               v.address?.trim() || null,
      phone:                 v.phone?.trim() || '',
      email:                 v.email?.trim() || null,
      joinedDate:            v.joinedDate,
      monthlyFee:            v.monthlyFee !== '' && v.monthlyFee != null ? Number(v.monthlyFee) : null,
      status:                v.status,
      dateOfBirth:           v.dateOfBirth || null,
      gender:                v.gender || null,
      occupation:            v.occupation?.trim() || null,
      householdId:           v.householdId?.trim() || null,
      householdRole:         v.householdRole || null,
      emergencyContactName:  v.emergencyContactName?.trim() || null,
      emergencyContactPhone: v.emergencyContactPhone?.trim() || null,
      location:              this.locationToPayload(),
      notes:                 v.notes?.trim() || null,
      customFields:          this.cf?.getValues() ?? {}
    };

    // Backend now always provisions a Default User account on member create.
    const createPayload: CreateMemberRequest = { ...payloadBase };

    this.isSubmitting = true;
    this.errorMessage = '';

    if (this.isEditMode && this.member?.id != null) {
      const req: UpdateMemberRequest = { ...payloadBase, status: payloadBase.status };
      this.memberService.updateMember(this.member.id, req).subscribe({
        next: (res) => {
          this.isSubmitting = false;
          if (res.success) {
            this.saved.emit(res.data);
          } else {
            this.errorMessage = res.message || 'Update failed.';
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.errorMessage = err?.error?.message || 'Update failed. Please try again.';
        }
      });
    } else {
      if (!this.entitlement.withinLimit('members')) {
        this.isSubmitting = false;
        const m = this.entitlement.metric('members');
        this.planLimitModal.showLimit({ limit: 'MaxMembers', current: m?.current, max: m?.max ?? null });
        return;
      }
      this.memberService.createMember(createPayload).subscribe({
        next: (res) => {
          this.isSubmitting = false;
          if (res.success) {
            const linked = !!res.userAccountLinked;
            const reactivated = !!res.userAccountReactivated;
            // Emit whenever a user account was created OR linked (parent decides UX).
            if (res.tempPassword || linked) {
              this.userProvisioned.emit({
                tempPassword: res.tempPassword ?? null,
                username: res.data?.userAccount?.username ?? null,
                credentialsDelivered: res.credentialsDelivered ?? null,
                linked,
                reactivated
              });
            }
            this.saved.emit(res.data);
          } else if (!isGatedErrorCode(res.code)) {
            this.errorMessage = res.message || 'Create failed.';
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.errorMessage = err?.error?.message || 'Create failed. Please try again.';
        }
      });
    }
  }

  private resetForm(): void {
    this.errorMessage = '';
    if (this.member) {
      this.form.reset({
        fullName:              this.member.fullName,
        nic:                   this.member.nic,
        dateOfBirth:           this.toDateInput(this.member.dateOfBirth),
        gender:                this.member.gender ?? null,
        occupation:            this.member.occupation ?? '',
        phone:                 this.member.phone,
        email:                 this.member.email ?? '',
        address:               this.member.address ?? '',
        householdId:           this.member.householdId ?? '',
        householdRole:         this.member.householdRole ?? null,
        emergencyContactName:  this.member.emergencyContactName ?? '',
        emergencyContactPhone: this.member.emergencyContactPhone ?? '',
        joinedDate:            this.toDateInput(this.member.joinedDate) || this.today(),
        monthlyFee:            this.member.monthlyFee ?? null,
        status:                this.member.status || 'Active',
        notes:                 this.member.notes ?? ''
      });
      this.location = this.member.location ?? null;
      this.selectedBuIds = new Set(this.member.businessUnitIds || []);
    } else {
      this.form.reset({
        fullName: '', nic: '', dateOfBirth: null, gender: null, occupation: '',
        phone: '', email: '', address: '',
        householdId: '', householdRole: null,
        emergencyContactName: '', emergencyContactPhone: '',
        joinedDate: this.today(), monthlyFee: null, status: 'Active', notes: ''
      });
      this.location = null;
      const currentBu = this.tenantContext.getCurrentBusinessUnitId();
      this.selectedBuIds = new Set(currentBu != null ? [currentBu] : []);
    }
  }

  private today(): string {
    const d = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private toDateInput(value?: string | null): string | null {
    if (!value) return null;
    return value.length >= 10 ? value.substring(0, 10) : value;
  }

  private locationToPayload(): MemberLocation | null {
    if (!this.location) return null;
    if (this.location.latitude == null || this.location.longitude == null) return null;
    return {
      latitude: this.location.latitude,
      longitude: this.location.longitude,
      formattedAddress: this.location.formattedAddress ?? this.form.value.address?.trim() ?? null
    };
  }
}
